import { Injectable } from "@angular/core";
import { FormControl } from "@angular/forms";
import { OwlDateTimeIntl } from "@danielmoncada/angular-datetime-picker";

export const SagaVocabularyTypeDisposition = 'urn:epcglobal:epcis:vtype:Disposition'
export const SagaVocabularyTypeBusinessLocation = 'urn:epcglobal:epcis:vtype:BusinessLocation'
export const SagaVocabularyTypeDestination = 'urn:epcglobal:epcis:vtype:SourceDest'
export const SagaVocabularyTypeErrorReason = 'urn:epcglobal:epcis:vtype:ErrorReason'
export const SagaVocabularyTypeBusinessStep = 'urn:epcglobal:epcis:vtype:BusinessStep'
export const SagaVocabularyTypeBusinessTransactionType = 'urn:epcglobal:epcis:vtype:BusinessTransactionType'
export const SagaVocabularyTypeSourceDestType = 'urn:epcglobal:epcis:vtype:SourceDestType'
export const SagaVocabularyTypeMarket = 'urn:psqr:extension:vtype:Market'
export const SagaVocabularyTypeEPCClass = 'urn:epcglobal:epcis:vtype:EPCClass'
export const SagaVocabularyTypeUnitConversion = 'urn:saga:extension:vtype:UnitConversion'
export const SagaVocabularyTypeRoles = 'urn:saga:extension:vtype:Roles'
export const SagaVocabularyTypeReadPoint = 'urn:epcglobal:epcis:vtype:ReadPoint'
export const SagaVocabularyTypeBusinessTransaction = 'urn:epcglobal:epcis:vtype:BusinessTransaction'

export const SagaVocabularyContainerTypes = [
    {
        'name': 's.containerType.Container',
        'value': 'Container',
    },
    {
        'name': 's.containerType.Bundle',
        'value': 'Bundle',
    },
    {
        'name': 's.containerType.Pack',
        'value': 'Pack',
    },
    {
        'name': 's.containerType.Pallet',
        'value': 'Pallet',
    },
    {
        'name': 's.containerType.Case',
        'value': 'Case',
    }
]

export const SourceTypes = [
    {
        name: 'Saga',
        value: 'saga'
    },
    {
        name: 'QR Trace',
        value: 'qrtrace'
    }
]

export const GeneralStatuses: { name: string; value: string }[] = [
    {
        name: 's.status.pending',
        value: 'pending'
    },
    {
        name: 's.status.progress',
        value: 'progress'
    },
    {
        name: 's.status.complete',
        value: 'complete'
    }
];

// Certificate types. Free text on the backend (Certificate.type has no vocabulary), so this
// list is the single source of truth for the UI — used by the certificate dialogs/lists and
// by the compliance rule editor, which names the cert type each check requires.
export const CertificateTypes: { value: string; label: string }[] = [
    { value: 'has', label: 'HAS (Halal Assurance System)' },
    { value: 'haccp', label: 'HACCP (Hazard Analysis and Critical Control Points)' },
];

export const CertificateIssuersByType: Record<string, string[]> = {
    has: ['JAKIM', 'HDC (Halal Development Corporation)', 'BERNAS', 'Other'],
    haccp: ['SIRIM QAS International', 'Bureau Veritas', 'SGS', 'Intertek', 'Lloyd\'s Register', 'Other'],
};

export const CertificateStatuses: string[] = ['active', 'pending', 'expired', 'revoked'];

// Falls back to the raw value so certificates carrying a type outside the list above
// still render something meaningful.
export const certificateTypeLabel = (type: string): string =>
    CertificateTypes.find(x => x.value === type)?.label ?? type;

export const TeamTagTypeGroup: string = 'team_group_tag';

// Below this remaining balance, the company-level blockchain wallet is considered low and an alert is shown
export const BlockchainWalletLowBalanceThreshold: number = 100;

export interface Country {
    name: string;
    alpha2: string;
    alpha3: string;
    countryCode: string;
}

export const Countries: Country[] = [
    { name: "Afghanistan", alpha2: "AF", alpha3: "AFG", countryCode: "+93" },
    { name: "Albania", alpha2: "AL", alpha3: "ALB", countryCode: "+355" },
    { name: "Algeria", alpha2: "DZ", alpha3: "DZA", countryCode: "+213" },
    { name: "American Samoa", alpha2: "AS", alpha3: "ASM", countryCode: "+1 684" },
    { name: "Andorra", alpha2: "AD", alpha3: "AND", countryCode: "+376" },
    { name: "Angola", alpha2: "AO", alpha3: "AGO", countryCode: "+244" },
    { name: "Anguilla", alpha2: "AI", alpha3: "AIA", countryCode: "+1 264" },
    { name: "Antarctica", alpha2: "AQ", alpha3: "ATA", countryCode: "+672" },
    { name: "Antigua and Barbuda", alpha2: "AG", alpha3: "ATG", countryCode: "+1 268" },
    { name: "Argentina", alpha2: "AR", alpha3: "ARG", countryCode: "+54" },
    { name: "Armenia", alpha2: "AM", alpha3: "ARM", countryCode: "+374" },
    { name: "Aruba", alpha2: "AW", alpha3: "ABW", countryCode: "+297" },
    { name: "Australia", alpha2: "AU", alpha3: "AUS", countryCode: "+61" },
    { name: "Austria", alpha2: "AT", alpha3: "AUT", countryCode: "+43" },
    { name: "Azerbaijan", alpha2: "AZ", alpha3: "AZE", countryCode: "+994" },
    { name: "Bahamas", alpha2: "BS", alpha3: "BHS", countryCode: "+1 242" },
    { name: "Bahrain", alpha2: "BH", alpha3: "BHR", countryCode: "+973" },
    { name: "Bangladesh", alpha2: "BD", alpha3: "BGD", countryCode: "+880" },
    { name: "Barbados", alpha2: "BB", alpha3: "BRB", countryCode: "+1 246" },
    { name: "Belarus", alpha2: "BY", alpha3: "BLR", countryCode: "+375" },
    { name: "Belgium", alpha2: "BE", alpha3: "BEL", countryCode: "+32" },
    { name: "Belize", alpha2: "BZ", alpha3: "BLZ", countryCode: "+501" },
    { name: "Benin", alpha2: "BJ", alpha3: "BEN", countryCode: "+229" },
    { name: "Bermuda", alpha2: "BM", alpha3: "BMU", countryCode: "+1 441" },
    { name: "Bhutan", alpha2: "BT", alpha3: "BTN", countryCode: "+975" },
    { name: "Bolivia (Plurinational State of)", alpha2: "BO", alpha3: "BOL", countryCode: "+591" },
    { name: "Bonaire, Sint Eustatius and Saba", alpha2: "BQ", alpha3: "BES", countryCode: "+599" },
    { name: "Bosnia and Herzegovina", alpha2: "BA", alpha3: "BIH", countryCode: "+387" },
    { name: "Botswana", alpha2: "BW", alpha3: "BWA", countryCode: "+267" },
    { name: "Brazil", alpha2: "BR", alpha3: "BRA", countryCode: "+55" },
    { name: "British Indian Ocean Territory", alpha2: "IO", alpha3: "IOT", countryCode: "+246" },
    { name: "Brunei Darussalam", alpha2: "BN", alpha3: "BRN", countryCode: "+673" },
    { name: "Bulgaria", alpha2: "BG", alpha3: "BGR", countryCode: "+359" },
    { name: "Burkina Faso", alpha2: "BF", alpha3: "BFA", countryCode: "+226" },
    { name: "Burundi", alpha2: "BI", alpha3: "BDI", countryCode: "+257" },
    { name: "Cabo Verde", alpha2: "CV", alpha3: "CPV", countryCode: "+238" },
    { name: "Cambodia", alpha2: "KH", alpha3: "KHM", countryCode: "+855" },
    { name: "Cameroon", alpha2: "CM", alpha3: "CMR", countryCode: "+237" },
    { name: "Canada", alpha2: "CA", alpha3: "CAN", countryCode: "+1" },
    { name: "Cayman Islands", alpha2: "KY", alpha3: "CYM", countryCode: "+1 345" },
    { name: "Central African Republic", alpha2: "CF", alpha3: "CAF", countryCode: "+236" },
    { name: "Chad", alpha2: "TD", alpha3: "TCD", countryCode: "+235" },
    { name: "Chile", alpha2: "CL", alpha3: "CHL", countryCode: "+56" },
    { name: "China", alpha2: "CN", alpha3: "CHN", countryCode: "+86" },
    { name: "Christmas Island", alpha2: "CX", alpha3: "CXR", countryCode: "+61" },
    { name: "Cocos (Keeling) Islands", alpha2: "CC", alpha3: "CCK", countryCode: "+61" },
    { name: "Colombia", alpha2: "CO", alpha3: "COL", countryCode: "+57" },
    { name: "Comoros", alpha2: "KM", alpha3: "COM", countryCode: "+269" },
    { name: "Congo", alpha2: "CG", alpha3: "COG", countryCode: "+242" },
    { name: "Congo (Democratic Republic of the)", alpha2: "CD", alpha3: "COD", countryCode: "+243" },
    { name: "Cook Islands", alpha2: "CK", alpha3: "COK", countryCode: "+682" },
    { name: "Costa Rica", alpha2: "CR", alpha3: "CRI", countryCode: "+506" },
    { name: "Côte d'Ivoire", alpha2: "CI", alpha3: "CIV", countryCode: "+225" },
    { name: "Croatia", alpha2: "HR", alpha3: "HRV", countryCode: "+385" },
    { name: "Cuba", alpha2: "CU", alpha3: "CUB", countryCode: "+53" },
    { name: "Curaçao", alpha2: "CW", alpha3: "CUW", countryCode: "+599" },
    { name: "Cyprus", alpha2: "CY", alpha3: "CYP", countryCode: "+357" },
    { name: "Czechia", alpha2: "CZ", alpha3: "CZE", countryCode: "+420" },
    { name: "Denmark", alpha2: "DK", alpha3: "DNK", countryCode: "+45" },
    { name: "Djibouti", alpha2: "DJ", alpha3: "DJI", countryCode: "+253" },
    { name: "Dominica", alpha2: "DM", alpha3: "DMA", countryCode: "+1 767" },
    { name: "Dominican Republic", alpha2: "DO", alpha3: "DOM", countryCode: "+1 809, +1 829, +1 849" },
    { name: "Ecuador", alpha2: "EC", alpha3: "ECU", countryCode: "+593" },
    { name: "Egypt", alpha2: "EG", alpha3: "EGY", countryCode: "+20" },
    { name: "El Salvador", alpha2: "SV", alpha3: "SLV", countryCode: "+503" },
    { name: "Equatorial Guinea", alpha2: "GQ", alpha3: "GNQ", countryCode: "+240" },
    { name: "Eritrea", alpha2: "ER", alpha3: "ERI", countryCode: "+291" },
    { name: "Estonia", alpha2: "EE", alpha3: "EST", countryCode: "+372" },
    { name: "Eswatini", alpha2: "SZ", alpha3: "SWZ", countryCode: "+268" },
    { name: "Ethiopia", alpha2: "ET", alpha3: "ETH", countryCode: "+251" },
    { name: "Falkland Islands (Malvinas)", alpha2: "FK", alpha3: "FLK", countryCode: "+500" },
    { name: "Faroe Islands", alpha2: "FO", alpha3: "FRO", countryCode: "+298" },
    { name: "Fiji", alpha2: "FJ", alpha3: "FJI", countryCode: "+679" },
    { name: "Finland", alpha2: "FI", alpha3: "FIN", countryCode: "+358" },
    { name: "France", alpha2: "FR", alpha3: "FRA", countryCode: "+33" },
    { name: "French Guiana", alpha2: "GF", alpha3: "GUF", countryCode: "+594" },
    { name: "French Polynesia", alpha2: "PF", alpha3: "PYF", countryCode: "+689" },
    { name: "Gabon", alpha2: "GA", alpha3: "GAB", countryCode: "+241" },
    { name: "Gambia", alpha2: "GM", alpha3: "GMB", countryCode: "+220" },
    { name: "Georgia", alpha2: "GE", alpha3: "GEO", countryCode: "+995" },
    { name: "Germany", alpha2: "DE", alpha3: "DEU", countryCode: "+49" },
    { name: "Ghana", alpha2: "GH", alpha3: "GHA", countryCode: "+233" },
    { name: "Gibraltar", alpha2: "GI", alpha3: "GIB", countryCode: "+350" },
    { name: "Greece", alpha2: "GR", alpha3: "GRC", countryCode: "+30" },
    { name: "Greenland", alpha2: "GL", alpha3: "GRL", countryCode: "+299" },
    { name: "Grenada", alpha2: "GD", alpha3: "GRD", countryCode: "+1 473" },
    { name: "Guadeloupe", alpha2: "GP", alpha3: "GLP", countryCode: "+590" },
    { name: "Guam", alpha2: "GU", alpha3: "GUM", countryCode: "+1 671" },
    { name: "Guatemala", alpha2: "GT", alpha3: "GTM", countryCode: "+502" },
    { name: "Guernsey", alpha2: "GG", alpha3: "GGY", countryCode: "+44" },
    { name: "Guinea", alpha2: "GN", alpha3: "GIN", countryCode: "+224" },
    { name: "Guinea-Bissau", alpha2: "GW", alpha3: "GNB", countryCode: "+245" },
    { name: "Guyana", alpha2: "GY", alpha3: "GUY", countryCode: "+592" },
    { name: "Haiti", alpha2: "HT", alpha3: "HTI", countryCode: "+509" },
    { name: "Holy See", alpha2: "VA", alpha3: "VAT", countryCode: "+379" },
    { name: "Honduras", alpha2: "HN", alpha3: "HND", countryCode: "+504" },
    { name: "Hong Kong", alpha2: "HK", alpha3: "HKG", countryCode: "+852" },
    { name: "Hungary", alpha2: "HU", alpha3: "HUN", countryCode: "+36" },
    { name: "Iceland", alpha2: "IS", alpha3: "ISL", countryCode: "+354" },
    { name: "India", alpha2: "IN", alpha3: "IND", countryCode: "+91" },
    { name: "Indonesia", alpha2: "ID", alpha3: "IDN", countryCode: "+62" },
    { name: "Iran (Islamic Republic of)", alpha2: "IR", alpha3: "IRN", countryCode: "+98" },
    { name: "Iraq", alpha2: "IQ", alpha3: "IRQ", countryCode: "+964" },
    { name: "Ireland", alpha2: "IE", alpha3: "IRL", countryCode: "+353" },
    { name: "Isle of Man", alpha2: "IM", alpha3: "IMN", countryCode: "+44" },
    { name: "Israel", alpha2: "IL", alpha3: "ISR", countryCode: "+972" },
    { name: "Italy", alpha2: "IT", alpha3: "ITA", countryCode: "+39" },
    { name: "Jamaica", alpha2: "JM", alpha3: "JAM", countryCode: "+1 876" },
    { name: "Japan", alpha2: "JP", alpha3: "JPN", countryCode: "+81" },
    { name: "Jersey", alpha2: "JE", alpha3: "JEY", countryCode: "+44" },
    { name: "Jordan", alpha2: "JO", alpha3: "JOR", countryCode: "+962" },
    { name: "Kazakhstan", alpha2: "KZ", alpha3: "KAZ", countryCode: "+7" },
    { name: "Kenya", alpha2: "KE", alpha3: "KEN", countryCode: "+254" },
    { name: "Kiribati", alpha2: "KI", alpha3: "KIR", countryCode: "+686" },
    { name: "Korea (Democratic People's Republic of)", alpha2: "KP", alpha3: "PRK", countryCode: "+850" },
    { name: "Korea (Republic of)", alpha2: "KR", alpha3: "KOR", countryCode: "+82" },
    { name: "Kuwait", alpha2: "KW", alpha3: "KWT", countryCode: "+965" },
    { name: "Kyrgyzstan", alpha2: "KG", alpha3: "KGZ", countryCode: "+996" },
    { name: "Lao People's Democratic Republic", alpha2: "LA", alpha3: "LAO", countryCode: "+856" },
    { name: "Latvia", alpha2: "LV", alpha3: "LVA", countryCode: "+371" },
    { name: "Lebanon", alpha2: "LB", alpha3: "LBN", countryCode: "+961" },
    { name: "Lesotho", alpha2: "LS", alpha3: "LSO", countryCode: "+266" },
    { name: "Liberia", alpha2: "LR", alpha3: "LBR", countryCode: "+231" },
    { name: "Libya", alpha2: "LY", alpha3: "LBY", countryCode: "+218" },
    { name: "Liechtenstein", alpha2: "LI", alpha3: "LIE", countryCode: "+423" },
    { name: "Lithuania", alpha2: "LT", alpha3: "LTU", countryCode: "+370" },
    { name: "Luxembourg", alpha2: "LU", alpha3: "LUX", countryCode: "+352" },
    { name: "Macao", alpha2: "MO", alpha3: "MAC", countryCode: "+853" },
    { name: "Madagascar", alpha2: "MG", alpha3: "MDG", countryCode: "+261" },
    { name: "Malawi", alpha2: "MW", alpha3: "MWI", countryCode: "+265" },
    { name: "Malaysia", alpha2: "MY", alpha3: "MYS", countryCode: "+60" },
    { name: "Maldives", alpha2: "MV", alpha3: "MDV", countryCode: "+960" },
    { name: "Mali", alpha2: "ML", alpha3: "MLI", countryCode: "+223" },
    { name: "Malta", alpha2: "MT", alpha3: "MLT", countryCode: "+356" },
    { name: "Marshall Islands", alpha2: "MH", alpha3: "MHL", countryCode: "+692" },
    { name: "Martinique", alpha2: "MQ", alpha3: "MTQ", countryCode: "+596" },
    { name: "Mauritania", alpha2: "MR", alpha3: "MRT", countryCode: "+222" },
    { name: "Mauritius", alpha2: "MU", alpha3: "MUS", countryCode: "+230" },
    { name: "Mayotte", alpha2: "YT", alpha3: "MYT", countryCode: "+262" },
    { name: "Mexico", alpha2: "MX", alpha3: "MEX", countryCode: "+52" },
    { name: "Micronesia (Federated States of)", alpha2: "FM", alpha3: "FSM", countryCode: "+691" },
    { name: "Moldova (Republic of)", alpha2: "MD", alpha3: "MDA", countryCode: "+373" },
    { name: "Monaco", alpha2: "MC", alpha3: "MCO", countryCode: "+377" },
    { name: "Mongolia", alpha2: "MN", alpha3: "MNG", countryCode: "+976" },
    { name: "Montenegro", alpha2: "ME", alpha3: "MNE", countryCode: "+382" },
    { name: "Montserrat", alpha2: "MS", alpha3: "MSR", countryCode: "+1 664" },
    { name: "Morocco", alpha2: "MA", alpha3: "MAR", countryCode: "+212" },
    { name: "Mozambique", alpha2: "MZ", alpha3: "MOZ", countryCode: "+258" },
    { name: "Myanmar", alpha2: "MM", alpha3: "MMR", countryCode: "+95" },
    { name: "Namibia", alpha2: "NA", alpha3: "NAM", countryCode: "+264" },
    { name: "Nauru", alpha2: "NR", alpha3: "NRU", countryCode: "+674" },
    { name: "Nepal", alpha2: "NP", alpha3: "NPL", countryCode: "+977" },
    { name: "Netherlands", alpha2: "NL", alpha3: "NLD", countryCode: "+31" },
    { name: "New Caledonia", alpha2: "NC", alpha3: "NCL", countryCode: "+687" },
    { name: "New Zealand", alpha2: "NZ", alpha3: "NZL", countryCode: "+64" },
    { name: "Nicaragua", alpha2: "NI", alpha3: "NIC", countryCode: "+505" },
    { name: "Niger", alpha2: "NE", alpha3: "NER", countryCode: "+227" },
    { name: "Nigeria", alpha2: "NG", alpha3: "NGA", countryCode: "+234" },
    { name: "Niue", alpha2: "NU", alpha3: "NIU", countryCode: "+683" },
    { name: "Norfolk Island", alpha2: "NF", alpha3: "NFK", countryCode: "+672" },
    { name: "North Macedonia", alpha2: "MK", alpha3: "MKD", countryCode: "+389" },
    { name: "Northern Mariana Islands", alpha2: "MP", alpha3: "MNP", countryCode: "+1 670" },
    { name: "Norway", alpha2: "NO", alpha3: "NOR", countryCode: "+47" },
    { name: "Oman", alpha2: "OM", alpha3: "OMN", countryCode: "+968" },
    { name: "Pakistan", alpha2: "PK", alpha3: "PAK", countryCode: "+92" },
    { name: "Palau", alpha2: "PW", alpha3: "PLW", countryCode: "+680" },
    { name: "Palestine, State of", alpha2: "PS", alpha3: "PSE", countryCode: "+970" },
    { name: "Panama", alpha2: "PA", alpha3: "PAN", countryCode: "+507" },
    { name: "Papua New Guinea", alpha2: "PG", alpha3: "PNG", countryCode: "+675" },
    { name: "Paraguay", alpha2: "PY", alpha3: "PRY", countryCode: "+595" },
    { name: "Peru", alpha2: "PE", alpha3: "PER", countryCode: "+51" },
    { name: "Philippines", alpha2: "PH", alpha3: "PHL", countryCode: "+63" },
    { name: "Pitcairn", alpha2: "PN", alpha3: "PCN", countryCode: "+64" },
    { name: "Poland", alpha2: "PL", alpha3: "POL", countryCode: "+48" },
    { name: "Portugal", alpha2: "PT", alpha3: "PRT", countryCode: "+351" },
    { name: "Puerto Rico", alpha2: "PR", alpha3: "PRI", countryCode: "+1 787, +1 939" },
    { name: "Qatar", alpha2: "QA", alpha3: "QAT", countryCode: "+974" },
    { name: "Réunion", alpha2: "RE", alpha3: "REU", countryCode: "+262" },
    { name: "Romania", alpha2: "RO", alpha3: "ROU", countryCode: "+40" },
    { name: "Russian Federation", alpha2: "RU", alpha3: "RUS", countryCode: "+7" },
    { name: "Rwanda", alpha2: "RW", alpha3: "RWA", countryCode: "+250" },
    { name: "Saint Barthélemy", alpha2: "BL", alpha3: "BLM", countryCode: "+590" },
    { name: "Saint Helena, Ascension and Tristan da Cunha", alpha2: "SH", alpha3: "SHN", countryCode: "+290" },
    { name: "Saint Kitts and Nevis", alpha2: "KN", alpha3: "KNA", countryCode: "+1 869" },
    { name: "Saint Lucia", alpha2: "LC", alpha3: "LCA", countryCode: "+1 758" },
    { name: "Saint Martin (French part)", alpha2: "MF", alpha3: "MAF", countryCode: "+590" },
    { name: "Saint Pierre and Miquelon", alpha2: "PM", alpha3: "SPM", countryCode: "+508" },
    { name: "Saint Vincent and the Grenadines", alpha2: "VC", alpha3: "VCT", countryCode: "+1 784" },
    { name: "Samoa", alpha2: "WS", alpha3: "WSM", countryCode: "+685" },
    { name: "San Marino", alpha2: "SM", alpha3: "SMR", countryCode: "+378" },
    { name: "Sao Tome and Principe", alpha2: "ST", alpha3: "STP", countryCode: "+239" },
    { name: "Saudi Arabia", alpha2: "SA", alpha3: "SAU", countryCode: "+966" },
    { name: "Senegal", alpha2: "SN", alpha3: "SEN", countryCode: "+221" },
    { name: "Serbia", alpha2: "RS", alpha3: "SRB", countryCode: "+381" },
    { name: "Seychelles", alpha2: "SC", alpha3: "SYC", countryCode: "+248" },
    { name: "Sierra Leone", alpha2: "SL", alpha3: "SLE", countryCode: "+232" },
    { name: "Singapore", alpha2: "SG", alpha3: "SGP", countryCode: "+65" },
    { name: "Sint Maarten (Dutch part)", alpha2: "SX", alpha3: "SXM", countryCode: "+1 721" },
    { name: "Slovakia", alpha2: "SK", alpha3: "SVK", countryCode: "+421" },
    { name: "Slovenia", alpha2: "SI", alpha3: "SVN", countryCode: "+386" },
    { name: "Solomon Islands", alpha2: "SB", alpha3: "SLB", countryCode: "+677" },
    { name: "Somalia", alpha2: "SO", alpha3: "SOM", countryCode: "+252" },
    { name: "South Africa", alpha2: "ZA", alpha3: "ZAF", countryCode: "+27" },
    { name: "South Sudan", alpha2: "SS", alpha3: "SSD", countryCode: "+211" },
    { name: "Spain", alpha2: "ES", alpha3: "ESP", countryCode: "+34" },
    { name: "Sri Lanka", alpha2: "LK", alpha3: "LKA", countryCode: "+94" },
    { name: "Sudan", alpha2: "SD", alpha3: "SDN", countryCode: "+249" },
    { name: "Suriname", alpha2: "SR", alpha3: "SUR", countryCode: "+597" },
    { name: "Sweden", alpha2: "SE", alpha3: "SWE", countryCode: "+46" },
    { name: "Switzerland", alpha2: "CH", alpha3: "CHE", countryCode: "+41" },
    { name: "Syrian Arab Republic", alpha2: "SY", alpha3: "SYR", countryCode: "+963" },
    { name: "Taiwan, Province of China", alpha2: "TW", alpha3: "TWN", countryCode: "+886" },
    { name: "Tajikistan", alpha2: "TJ", alpha3: "TJK", countryCode: "+992" },
    { name: "Tanzania, United Republic of", alpha2: "TZ", alpha3: "TZA", countryCode: "+255" },
    { name: "Thailand", alpha2: "TH", alpha3: "THA", countryCode: "+66" },
    { name: "Timor-Leste", alpha2: "TL", alpha3: "TLS", countryCode: "+670" },
    { name: "Togo", alpha2: "TG", alpha3: "TGO", countryCode: "+228" },
    { name: "Tokelau", alpha2: "TK", alpha3: "TKL", countryCode: "+690" },
    { name: "Tonga", alpha2: "TO", alpha3: "TON", countryCode: "+676" },
    { name: "Trinidad and Tobago", alpha2: "TT", alpha3: "TTO", countryCode: "+1 868" },
    { name: "Tunisia", alpha2: "TN", alpha3: "TUN", countryCode: "+216" },
    { name: "Turkey", alpha2: "TR", alpha3: "TUR", countryCode: "+90" },
    { name: "Turkmenistan", alpha2: "TM", alpha3: "TKM", countryCode: "+993" },
    { name: "Turks and Caicos Islands", alpha2: "TC", alpha3: "TCA", countryCode: "+1 649" },
    { name: "Tuvalu", alpha2: "TV", alpha3: "TUV", countryCode: "+688" },
    { name: "Uganda", alpha2: "UG", alpha3: "UGA", countryCode: "+256" },
    { name: "Ukraine", alpha2: "UA", alpha3: "UKR", countryCode: "+380" },
    { name: "United Arab Emirates", alpha2: "AE", alpha3: "ARE", countryCode: "+971" },
    { name: "United Kingdom of Great Britain and Northern Ireland", alpha2: "GB", alpha3: "GBR", countryCode: "+44" },
    { name: "United States of America", alpha2: "US", alpha3: "USA", countryCode: "+1" },
    { name: "Uruguay", alpha2: "UY", alpha3: "URY", countryCode: "+598" },
    { name: "Uzbekistan", alpha2: "UZ", alpha3: "UZB", countryCode: "+998" },
    { name: "Vanuatu", alpha2: "VU", alpha3: "VUT", countryCode: "+678" },
    { name: "Venezuela (Bolivarian Republic of)", alpha2: "VE", alpha3: "VEN", countryCode: "+58" },
    { name: "Viet Nam", alpha2: "VN", alpha3: "VNM", countryCode: "+84" },
    { name: "Virgin Islands (British)", alpha2: "VG", alpha3: "VGB", countryCode: "+1 284" },
    { name: "Virgin Islands (U.S.)", alpha2: "VI", alpha3: "VIR", countryCode: "+1 340" },
    { name: "Wallis and Futuna", alpha2: "WF", alpha3: "WLF", countryCode: "+681" },
    { name: "Western Sahara", alpha2: "EH", alpha3: "ESH", countryCode: "+212" },
    { name: "Yemen", alpha2: "YE", alpha3: "YEM", countryCode: "+967" },
    { name: "Zambia", alpha2: "ZM", alpha3: "ZMB", countryCode: "+260" },
    { name: "Zimbabwe", alpha2: "ZW", alpha3: "ZWE", countryCode: "+263" }
];

export const demoEmails: string[] = [
    "kapitani@gmail.com"
]

//api
export const TeamGroupFields: string = 'id,uuid,name,meta,created,last_modified,member_count';
export const TeamGroupSimpleFields: string = 'id,uuid,name';

//function
export const ConvertRGBAToHex = (r: number, g: number, b: number, a: number) => {
    const rHex = r.toString(16).padStart(2, '0');;
    const gHex = g.toString(16).padStart(2, '0');;
    const bHex = b.toString(16).padStart(2, '0');;
    // const alpha = Math.round(a * 255).toString(16).padStart(2, '0');

    return '#' + rHex + gHex + bHex;
}

export const ValidateCountrySelection = (control: FormControl) => {
    const selection = control.value;
    if (!selection) return null;

    if (typeof selection === 'string') {
        return { invalidSelection: true };
    }

    return null;
}

//date
@Injectable()
export class OwnlDateTimeDefaultIntl extends OwlDateTimeIntl {
    setBtnLabel = 'Apply'
};